import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Footer from '../components/Footer';
import ReCAPTCHA from 'react-google-recaptcha';

function ProgramPage() {
    const { programName } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const programId = location.state?.programId;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [recaptchaValue, setRecaptchaValue] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [programData, setProgramData] = useState(null);
    const [selectedParticipationDay, setSelectedParticipationDay] = useState(null);
    const [bannerImages, setBannerImages] = useState([]);
    const [formErrors, setFormErrors] = useState({});
    const [submittedData, setSubmittedData] = useState(null);

    // API URLs
    const API_BASE = `${process.env.REACT_APP_API_BASE_URL}`;
    const PROGRAM_API = `${API_BASE}/UI/program`;
    const MEDIA_API = `${API_BASE}/UImedia`;
    const REGISTRATION_API = `${API_BASE}/program-registration`;

    // reCAPTCHA site key
    const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        unitName: '',
        udyamNumber: '',
        mobileNumber: ''
    });

    // Fetch data from API
    useEffect(() => {
        if (programId) {
            fetchProgramData();
        } else {
            setError('No program ID provided');
            setLoading(false);
        }
    }, [programId]);

    const fetchProgramData = async () => {
        try {
            setLoading(true);

            // Fetch all and filter by ID
            const response = await axios.get(`${API_BASE}/UI/all-programs`);

            if (response.data.success) {
                const allPrograms = response.data.data;

                // Find the specific program by ID
                const foundProgram = allPrograms.find(program => program.id === programId);

                if (foundProgram) {
                    // console.log("Found Program:", foundProgram);

                    // Set selected participation day
                    const participationDays = foundProgram.participationDays || [];
                    const selectedDay = participationDays.find(day => day.isSelected);
                    if (selectedDay) {
                        setSelectedParticipationDay(selectedDay.label);
                    }

                    // Fetch banner images
                    if (foundProgram.bannerImages && foundProgram.bannerImages.length > 0) {
                        await fetchBannerImages(foundProgram.bannerImages);
                    }

                    setProgramData(foundProgram);
                } else {
                    setError('Program not found');
                }
            } else {
                setError('Failed to load program data');
            }
        } catch (err) {
            console.error('Error fetching program data:', err);
            setError('Error loading program data. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    // Fetch banner images from Media collection
    const fetchBannerImages = async (bannerImagesArray) => {
        try {
            const imageUrls = [];

            for (const banner of bannerImagesArray) {
                if (banner.mediaId) {
                    try {
                        // Check if mediaId is object or string
                        const mediaId = banner.mediaId.id || banner.mediaId;

                        const response = await axios.get(
                            `${MEDIA_API}/${mediaId}/download`,
                            {
                                responseType: 'blob',
                                timeout: 10000
                            }
                        );

                        if (response.data && response.status === 200) {
                            const imageUrl = URL.createObjectURL(response.data);
                            imageUrls.push(imageUrl);
                        }
                    } catch (imgErr) {
                        console.error('Error fetching banner image:', imgErr);
                        // Add fallback image
                        imageUrls.push(`https://via.placeholder.com/1920x1080/3B82F6/FFFFFF?text=Program+Banner`);
                    }
                }
            }

            // If no images fetched, use fallback
            if (imageUrls.length === 0) {
                imageUrls.push(
                    'https://images.unsplash.com/photo-1523580494863-6f3031224c94?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80'
                );
            }

            setBannerImages(imageUrls);

        } catch (error) {
            console.error('Error in fetchBannerImages:', error);
            setBannerImages([
                'https://images.unsplash.com/photo-1523580494863-6f3031224c94?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80'
            ]);
        }
    };

    // Cleanup object URLs
    useEffect(() => {
        return () => {
            bannerImages.forEach(url => {
                if (url && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [bannerImages]);

    // Auto slide effect
    useEffect(() => {
        if (bannerImages.length > 1) {
            const interval = setInterval(() => {
                setCurrentSlide((prevSlide) => (prevSlide + 1) % bannerImages.length);
            }, 3000);

            return () => clearInterval(interval);
        }
    }, [bannerImages]);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        // Clear error for this field
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Handle participation day selection
    const handleParticipationDayChange = (day) => {
        setSelectedParticipationDay(day);
        if (formErrors.participationDay) {
            setFormErrors(prev => ({ ...prev, participationDay: '' }));
        }
    };

    // Handle reCAPTCHA change
    const handleRecaptchaChange = (value) => {
        setRecaptchaValue(value);
        if (formErrors.recaptcha) {
            setFormErrors(prev => ({ ...prev, recaptcha: '' }));
        }
    };

    const handleRecaptchaExpire = () => {
        setRecaptchaValue(null);
        setFormErrors(prev => ({ ...prev, recaptcha: 'reCAPTCHA has expired. Please verify again.' }));
    };

    // Validate form
    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) {
            errors.name = 'Please enter your name';
        }

        if (!formData.email.trim()) {
            errors.email = 'Please enter your email';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }

        if (!formData.mobileNumber.trim()) {
            errors.mobileNumber = 'Please enter your mobile number';
        } else if (!/^\d{10}$/.test(formData.mobileNumber)) {
            errors.mobileNumber = 'Please enter a valid 10-digit mobile number';
        }

        if (!selectedParticipationDay) {
            errors.participationDay = 'Please select participation days';
        }

        if (!recaptchaValue) {
            errors.recaptcha = 'Please complete the reCAPTCHA verification';
        }

        return errors;
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            
            // Scroll to first error
            const firstErrorField = Object.keys(errors)[0];
            const element = document.querySelector(`[name="${firstErrorField}"]`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.focus();
            }
            return;
        }

        setIsSubmitting(true);
        setFormErrors({});

        try {
            // Prepare submission data
            const submissionData = {
                name: formData.name.trim(),
                email: formData.email.trim(),
                mobileNumber: formData.mobileNumber.trim(),
                unitName: formData.unitName.trim(),
                udyamNumber: formData.udyamNumber.trim(),
                participationDay: selectedParticipationDay,
                programId: programId,
                programTitle: programData?.programInfo?.title || programName || 'Program',
                programDate: programData?.programInfo?.date,
                programVenue: programData?.programInfo?.venue,
                recaptchaToken: recaptchaValue,
                submittedAt: new Date().toISOString()
            };

            // console.log('Submitting data:', submissionData);

            // Submit to backend API
            const response = await axios.post(REGISTRATION_API, submissionData, {
                headers: {
                    'Content-Type': 'application/json',
                },
                timeout: 15000
            });

            if (response.data.success) {
                // Generate reference ID
                const referenceId = new Date().getTime().toString().slice(-8);
                
                // Store submitted data for success popup
                setSubmittedData({
                    ...submissionData,
                    referenceId: referenceId
                });
                
                // Show success popup
                setShowSuccessPopup(true);

            } else {
                throw new Error(response.data.message || 'Registration failed');
            }

        } catch (err) {
            console.error('Registration error:', err);
            
            if (err.response) {
                const errorMessage = err.response.data?.message || 'Registration failed';
                
                if (err.response.status === 400 && errorMessage.includes('already registered')) {
                    alert('You have already registered for this program with this email.');
                } else if (err.response.status === 400) {
                    alert(errorMessage);
                } else if (err.response.status === 500) {
                    alert('Server error. Please try again later.');
                } else {
                    alert(errorMessage);
                }
            } else if (err.request) {
                alert('Network error. Please check your internet connection and try again.');
            } else {
                alert('Error submitting form. Please try again.');
            }

            setRecaptchaValue(null);
            if (window.grecaptcha) {
                window.grecaptcha.reset();
            }

        } finally {
            setIsSubmitting(false);
        }
    };

    // Reset form after closing success popup
    const handleCloseSuccessPopup = () => {
        setShowSuccessPopup(false);
        setSubmittedData(null);
        
        // Reset form
        setFormData({
            name: '',
            email: '',
            unitName: '',
            udyamNumber: '',
            mobileNumber: ''
        });
        setSelectedParticipationDay(null);
        setRecaptchaValue(null);
        setFormErrors({});
        
        // Reset reCAPTCHA
        if (window.grecaptcha) {
            window.grecaptcha.reset();
        }
    };

    // Navigation functions for banner
    const goToSlide = (index) => {
        setCurrentSlide(index);
    };

    const prevSlide = () => {
        if (bannerImages.length > 0) {
            setCurrentSlide((prev) => (prev === 0 ? bannerImages.length - 1 : prev - 1));
        }
    };

    const nextSlide = () => {
        if (bannerImages.length > 0) {
            setCurrentSlide((prev) => (prev + 1) % bannerImages.length);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-white">Loading program...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error || !programData) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center justify-center">
                <div className="text-center p-4">
                    <div className="text-red-500 text-4xl mb-4">⚠️</div>
                    <h3 className="text-white text-xl mb-2">Error Loading Program</h3>
                    <p className="text-gray-400">{error || 'No program data available'}</p>
                    <button
                        onClick={fetchProgramData}
                        className="mt-4 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // Extract data from program
    const programInfo = programData.programInfo || {};
    const importantNotice = programData.importantNotice || {};
    const successPopup = programData.successPopup || {};
    const registrationSettings = programData.registrationSettings || {};
    const footerNotes = programData.footerNotes || [];
    const contactPersons = programData.contactPersons || [];
    const participationDays = programData.participationDays || [];

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
            {/* Image Slider Section */}
            <div className="relative w-full h-[400px] md:h-[650px] overflow-hidden">
                {/* Image Slides */}
                <div className="relative h-full">
                    {bannerImages.length > 0 ? (
                        bannerImages.map((image, index) => (
                            <div
                                key={index}
                                className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
                                    }`}
                            >
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        backgroundImage: `url(${image})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        backgroundRepeat: 'no-repeat'
                                    }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-blue-900 flex items-center justify-center z-10">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-2xl text-blue-400">📷</span>
                                </div>
                                <p className="text-gray-400 text-xl">{programInfo.programName || 'Program'}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation Arrows */}
                {bannerImages.length > 1 && (
                    <>
                        <button
                            onClick={prevSlide}
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-2 md:p-3 rounded-full transition-all hover:scale-110"
                            aria-label="Previous slide"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        <button
                            onClick={nextSlide}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 text-white p-2 md:p-3 rounded-full transition-all hover:scale-110"
                            aria-label="Next slide"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </>
                )}

                {/* Dots Indicator */}
                {bannerImages.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 flex gap-2">
                        {bannerImages.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                className={`w-3 h-3 rounded-full transition-all ${index === currentSlide
                                        ? 'bg-blue-400 scale-125'
                                        : 'bg-white/50 hover:bg-white/70'
                                    }`}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 py-8">
                <div className="max-w-6xl mx-auto text-center mb-8">
                    <h1 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                        {programInfo.title || programInfo.programName}
                    </h1>
                    {programInfo.subtitle && (
                        <p className="text-lg text-blue-300 mb-4">
                            {programInfo.subtitle}
                        </p>
                    )}
                    {programInfo.description && (
                        <p className="text-white mb-6 max-w-3xl mx-auto">
                            {programInfo.description}
                        </p>
                    )}
                    {(programInfo.date || programInfo.venue) && (
                        <p className="text-lg text-blue-200 font-medium">
                            {programInfo.date} {programInfo.venue && `at ${programInfo.venue}`}
                        </p>
                    )}
                </div>

                {/* Important Notice */}
                {importantNotice.content && (
                    <div className="mb-8 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                        {importantNotice.title && (
                            <p className="text-yellow-300 font-medium mb-2">
                                {importantNotice.title}:
                            </p>
                        )}
                        <p className="text-white text-sm">
                            {importantNotice.content}
                        </p>
                    </div>
                )}

                {/* Contact Information */}
                {contactPersons.length > 0 && (
                    <div className="mb-8 bg-blue-900/20 border border-blue-500/20 rounded-lg p-4">
                        <p className="text-blue-300 font-medium mb-2">For any queries please contact:</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {contactPersons.map((contact, index) => (
                                <p key={index} className="text-white text-sm">
                                    {contact.name}<br />{contact.phone}
                                </p>
                            ))}
                        </div>
                    </div>
                )}

                {/* Registration Form */}
                <section id="registration-form" className="bg-white/5 border border-white/10 rounded-lg p-6">
                    <h2 className="text-2xl font-bold mb-6 text-center text-blue-300">
                        Registration Form
                    </h2>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Email */}
                        <div>
                            <label className="block text-blue-300 mb-2 font-medium">
                                e-Mail of the Participant *
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                className={`w-full px-4 py-3 bg-white/5 border rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white outline-none ${formErrors.email ? 'border-red-500' : 'border-white/20'}`}
                                placeholder="Enter your email"
                            />
                            {formErrors.email && (
                                <p className="text-red-400 text-sm mt-1">{formErrors.email}</p>
                            )}
                        </div>

                        {/* Name */}
                        <div>
                            <label className="block text-blue-300 mb-2 font-medium">
                                Name of the Participant *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                className={`w-full px-4 py-3 bg-white/5 border rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white outline-none ${formErrors.name ? 'border-red-500' : 'border-white/20'}`}
                                placeholder="Enter your full name"
                            />
                            {formErrors.name && (
                                <p className="text-red-400 text-sm mt-1">{formErrors.name}</p>
                            )}
                        </div>

                        {/* Name of Unit */}
                        <div>
                            <label className="block text-blue-300 mb-2">
                                Name of the Unit
                            </label>
                            <input
                                type="text"
                                name="unitName"
                                value={formData.unitName}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white outline-none"
                                placeholder="Enter unit/organization name"
                            />
                        </div>

                        {/* Udyam Registration Number */}
                        <div>
                            <label className="block text-blue-300 mb-2">
                                Udyam Registration Number
                            </label>
                            <input
                                type="text"
                                name="udyamNumber"
                                value={formData.udyamNumber}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white outline-none"
                                placeholder="Enter Udyam Registration Number"
                            />
                        </div>

                        {/* Mobile Number */}
                        <div>
                            <label className="block text-blue-300 mb-2 font-medium">
                                Mobile Number *
                            </label>
                            <input
                                type="tel"
                                name="mobileNumber"
                                value={formData.mobileNumber}
                                onChange={handleInputChange}
                                required
                                pattern="[0-9]{10}"
                                className={`w-full px-4 py-3 bg-white/5 border rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-white outline-none ${formErrors.mobileNumber ? 'border-red-500' : 'border-white/20'}`}
                                placeholder="Enter 10-digit mobile number"
                            />
                            {formErrors.mobileNumber && (
                                <p className="text-red-400 text-sm mt-1">{formErrors.mobileNumber}</p>
                            )}
                        </div>

                        {/* Participate In */}
                        {participationDays.length > 0 && (
                            <div>
                                <label className="block text-blue-300 mb-3 font-medium">
                                    Participate in *
                                </label>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {participationDays.map((day, index) => (
                                        <label
                                            key={index}
                                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${selectedParticipationDay === day.label
                                                    ? 'bg-blue-500/20 border-2 border-blue-500'
                                                    : 'bg-white/10 border border-white/20 hover:bg-white/20'
                                                } ${formErrors.participationDay ? 'border-red-500/50' : ''}`}
                                        >
                                            <input
                                                type="radio"
                                                name="participationDays"
                                                value={day.label}
                                                checked={selectedParticipationDay === day.label}
                                                onChange={() => handleParticipationDayChange(day.label)}
                                                className="text-blue-500 cursor-pointer"
                                            />
                                            <span className="text-white">{day.label}</span>
                                        </label>
                                    ))}
                                </div>
                                {formErrors.participationDay && (
                                    <p className="text-red-400 text-sm mt-1">{formErrors.participationDay}</p>
                                )}
                            </div>
                        )}

                        {/* reCAPTCHA */}
                        <div className="pt-4">
                            <div className="flex justify-center">
                                <ReCAPTCHA
                                    sitekey={RECAPTCHA_SITE_KEY}
                                    onChange={handleRecaptchaChange}
                                    onExpired={handleRecaptchaExpire}
                                    theme="dark"
                                    size="normal"
                                />
                            </div>
                            {formErrors.recaptcha && (
                                <p className="text-red-400 text-sm mt-2 text-center">{formErrors.recaptcha}</p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <div className="text-center pt-4">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold py-3 px-6 rounded-lg transition ${isSubmitting
                                        ? 'opacity-50 cursor-not-allowed'
                                        : 'hover:from-blue-600 hover:to-blue-700'
                                    }`}
                            >
                                {isSubmitting ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        SUBMITTING...
                                    </span>
                                ) : (
                                    "Submit Registration Request"
                                )}
                            </button>
                            {registrationSettings.lastDate && (
                                <p className="mt-3 text-sm text-gray-400">
                                    Last date for submission: {registrationSettings.lastDate}
                                </p>
                            )}
                            {registrationSettings.venueRegistrationTime && (
                                <p className="mt-1 text-sm text-gray-400">
                                    Venue registration: {registrationSettings.venueRegistrationTime}
                                </p>
                            )}
                        </div>
                    </form>
                </section>

                {/* Footer Notes */}
                {footerNotes.length > 0 && (
                    <div className="mt-8 text-center text-gray-400 text-sm">
                        {footerNotes.map((note, index) => (
                            <p key={index} className={index > 0 ? 'mt-1' : ''}>
                                {note}
                            </p>
                        ))}
                    </div>
                )}
            </main>

            <Footer />

            {/* Success Popup */}
            {showSuccessPopup && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-blue-500/30 rounded-lg max-w-md w-full p-6">
                        <div className="text-center">
                            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                                </svg>
                            </div>

                            <h3 className="text-xl font-bold text-white mb-3">
                                {successPopup.title || 'Registration Successful!'}
                            </h3>

                            <p className="text-gray-300 mb-4 text-sm">
                                {successPopup.message || `Thank you for registering for ${programInfo?.title || 'the program'}.`}
                            </p>

                            <div className="bg-blue-500/10 border border-blue-500/20 rounded p-4 mb-4 text-left">
                                <p className="text-blue-300 text-sm font-medium mb-2">Registration Details:</p>
                                <div className="space-y-2 text-sm">
                                    <p className="text-white">
                                        <span className="text-gray-400">Name:</span> {submittedData?.name}
                                    </p>
                                    <p className="text-white">
                                        <span className="text-gray-400">Email:</span> {submittedData?.email}
                                    </p>
                                    <p className="text-white">
                                        <span className="text-gray-400">Mobile:</span> {submittedData?.mobileNumber}
                                    </p>
                                    <p className="text-white">
                                        <span className="text-gray-400">Program:</span> {submittedData?.programTitle || programInfo?.title}
                                    </p>
                                    <p className="text-white">
                                        <span className="text-gray-400">Participation Day:</span> {submittedData?.participationDay}
                                    </p>
                                    {submittedData?.unitName && (
                                        <p className="text-white">
                                            <span className="text-gray-400">Unit Name:</span> {submittedData.unitName}
                                        </p>
                                    )}
                                    {submittedData?.udyamNumber && (
                                        <p className="text-white">
                                            <span className="text-gray-400">Udyam Number:</span> {submittedData.udyamNumber}
                                        </p>
                                    )}
                                    <p className="text-white">
                                        <span className="text-gray-400">Reference ID:</span> {submittedData?.referenceId}
                                    </p>
                                    <p className="text-white text-xs text-gray-400 mt-2">
                                        Registration Date: {new Date().toLocaleDateString('en-IN')}
                                    </p>
                                </div>
                            </div>

                            {successPopup.importantNote && (
                                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded p-3 mb-4 text-left">
                                    <p className="text-yellow-300 text-sm font-medium mb-1">Important:</p>
                                    <p className="text-yellow-100 text-xs">
                                        {successPopup.importantNote}
                                    </p>
                                </div>
                            )}

                            <button
                                onClick={handleCloseSuccessPopup}
                                className="w-full bg-blue-500 text-white font-medium py-2 px-4 rounded hover:bg-blue-600 transition"
                            >
                                Close
                            </button>
                            
                            <p className="text-gray-400 text-xs mt-3">
                                A confirmation email has been sent to {submittedData?.email}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProgramPage;